import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useOrganization } from "@/hooks/useOrganization";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { subDays } from "date-fns";
import { getLocalDayBounds } from "@/lib/utils";
import { LayoutDashboard, DollarSign, BarChart3, UserCheck, Crown, Trophy, Receipt, Calculator } from "lucide-react";
import ReportDateRangePicker, { getComparisonRange, type ReportDateRange } from "./reports/ReportDateRangePicker";
import DashboardTab from "./reports/DashboardTab";
import RevenueTab from "./reports/RevenueTab";
import OccupancyTab from "./reports/OccupancyTab";
import PlayersTab from "./reports/PlayersTab";
import MembershipTab from "./reports/MembershipTab";
import ActivitiesTab from "./reports/ActivitiesTab";
import BudgetTab from "./reports/BudgetTab";
import ManagementReport from "./ManagementReport";
import PaymentReconciliation from "./reports/PaymentReconciliation";
import { splitMatchiPayments, excludeMatchiPayments } from "@/lib/matchiRevenue";

export default function AdminStatistics() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { hallId, hallHours, courts } = useOrgHall();

  const [range, setRange] = useState<ReportDateRange>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");

  const compRange = getComparisonRange(range);
  const rangeStart = getLocalDayBounds(range.from).start;
  const rangeEnd = getLocalDayBounds(range.to).end;
  const prevStart = getLocalDayBounds(compRange.from).start;
  const prevEnd = getLocalDayBounds(compRange.to).end;

  // === Core data queries (always loaded) ===
  const { data: bookings = [] } = useQuery({
    queryKey: ["report-bookings", hallId, rangeStart, rangeEnd],
    queryFn: async () => {
      // Sprint D aggregates: include _minor + currency cols.
      const { data } = await supabase
        .from("bookings")
        .select("id, start_time, end_time, status, court_id, duration_minutes, price_total, price_total_minor, price_per_hour, price_per_hour_minor, currency, user_id, courts!inner(name, hall_id)")
        .eq("courts.hall_id", hallId!)
        .gte("start_time", rangeStart)
        .lte("start_time", rangeEnd)
        .is("deleted_at", null)
        .order("start_time");
      return data ?? [];
    },
    enabled: !!hallId,
  });

  const { data: prevBookings = [] } = useQuery({
    queryKey: ["report-prev-bookings", hallId, prevStart, prevEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("bookings")
        .select("id, start_time, status, court_id, duration_minutes, price_total, price_total_minor, currency, user_id, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .gte("start_time", prevStart)
        .lte("start_time", prevEnd)
        .is("deleted_at", null);
      return data ?? [];
    },
    enabled: !!hallId && compareEnabled,
  });

  const { data: allPayments = [] } = useQuery({
    queryKey: ["report-payments", organization?.id, rangeStart, rangeEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("id, amount, amount_minor, currency, status, paid_at, created_at, payment_method, payment_method_id, payment_type")
        .eq("organization_id", organization!.id)
        .eq("status", "paid")
        .gte("created_at", rangeStart)
        .lte("created_at", rangeEnd);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: allPrevPayments = [] } = useQuery({
    queryKey: ["report-prev-payments", organization?.id, prevStart, prevEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_payments")
        .select("id, amount, amount_minor, currency, status, paid_at, created_at, payment_method, payment_method_id, payment_type")
        .eq("organization_id", organization!.id)
        .eq("status", "paid")
        .gte("created_at", prevStart)
        .lte("created_at", prevEnd);
      return data ?? [];
    },
    enabled: !!organization?.id && compareEnabled,
  });

  // C3/BES-004: splitten sker HÄR, vid källan, inte i varje tab. Alla nuvarande och
  // framtida flikar får därmed Boply-intäkt i `payments` utan att behöva känna till
  // MATCHi — särredovisningen matas separat till de flikar som visar totalsummor.
  const { boply: payments, matchi: matchiPayments } = useMemo(
    () => splitMatchiPayments(allPayments),
    [allPayments],
  );
  const prevPayments = useMemo(() => excludeMatchiPayments(allPrevPayments), [allPrevPayments]);

  const { data: courtCount = 0 } = useQuery({
    queryKey: ["report-courts-count", hallId],
    queryFn: async () => {
      const { count } = await supabase.from("courts").select("id", { count: "exact", head: true }).eq("hall_id", hallId!).eq("status", "active");
      return count ?? 0;
    },
    enabled: !!hallId,
  });

  const { data: userCount = 0 } = useQuery({
    queryKey: ["report-users", organization?.id],
    queryFn: async () => {
      const { count } = await supabase.from("organization_members").select("id", { count: "exact", head: true }).eq("organization_id", organization!.id);
      return count ?? 0;
    },
    enabled: !!organization?.id,
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["report-profiles"],
    queryFn: async () => {
      const { data } = await supabase.from("public_profiles" as any).select("id, full_name, created_at");
      return data ?? [];
    },
  });

  // === Memberships (for Dashboard MRR + Membership tab) ===
  const { data: memberships = [] } = useQuery({
    queryKey: ["report-memberships", organization?.id],
    queryFn: async () => {
      // Sprint D aggregates: include plan price_minor + currency for MRR calc.
      const { data } = await supabase
        .from("memberships")
        .select("id, user_id, status, created_at, current_period_start, current_period_end, plan:membership_plans(name, price, price_minor, currency, interval)")
        .eq("organization_id", organization!.id);
      return (data ?? []).map((m: any) => ({
        id: m.id,
        user_id: m.user_id,
        status: m.status,
        created_at: m.created_at,
        current_period_start: m.current_period_start,
        current_period_end: m.current_period_end,
        plan_name: m.plan?.name ?? null,
        plan_price: m.plan?.price ?? null,
        plan_price_minor: m.plan?.price_minor ?? null,
        plan_currency: m.plan?.currency ?? "SEK",
        plan_interval: m.plan?.interval ?? null,
      }));
    },
    enabled: !!organization?.id,
  });

  // === No-show records (for Dashboard) ===
  const { data: noShows = [] } = useQuery({
    queryKey: ["report-noshows", organization?.id, rangeStart, rangeEnd],
    queryFn: async () => {
      const { data } = await supabase
        .from("no_show_records")
        .select("id, created_at")
        .eq("organization_id", organization!.id)
        .gte("created_at", rangeStart)
        .lte("created_at", rangeEnd);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // === Activities data (only when activities tab active) ===
  const { data: leagues = [] } = useQuery({
    queryKey: ["report-leagues", organization?.id],
    queryFn: async () => {
      // Sprint D aggregates: include _minor + currency for dual-state read.
      const { data } = await supabase
        .from("leagues")
        .select("id, name, status, cost_per_player, cost_per_player_minor, court_cost_per_match, court_cost_per_match_minor, per_round_price, per_round_price_minor, per_round_price_per_player, per_round_price_per_player_minor, admin_fee_per_match, admin_fee_per_match_minor, currency")
        .eq("organization_id", organization!.id);
      return data ?? [];
    },
    enabled: !!organization?.id && activeTab === "activities",
  });

  const { data: leagueMatches = [] } = useQuery({
    queryKey: ["report-league-matches", leagues.map((l: any) => l.id)],
    queryFn: async () => {
      const ids = leagues.map((l: any) => l.id);
      if (ids.length === 0) return [];
      const { data } = await supabase
        .from("league_matches")
        .select("id, league_id, status")
        .in("league_id", ids);
      return data ?? [];
    },
    enabled: leagues.length > 0 && activeTab === "activities",
  });

  const { data: tournaments = [] } = useQuery({
    queryKey: ["report-tournaments", organization?.id],
    queryFn: async () => {
      // Sprint D aggregates: include _minor + currency.
      const { data } = await supabase
        .from("tournaments")
        .select("id, name, status, price_per_player, price_per_player_minor, admin_fee_per_player, admin_fee_per_player_minor, court_cost_total, court_cost_total_minor, currency, max_players")
        .eq("organization_id", organization!.id);
      return data ?? [];
    },
    enabled: !!organization?.id && activeTab === "activities",
  });

  const { data: tournamentPlayers = [] } = useQuery({
    queryKey: ["report-tournament-players", tournaments.map((t: any) => t.id)],
    queryFn: async () => {
      const ids = tournaments.map((t: any) => t.id);
      if (ids.length === 0) return [];
      const { data } = await supabase
        .from("tournament_players")
        .select("tournament_id")
        .in("tournament_id", ids);
      return data ?? [];
    },
    enabled: tournaments.length > 0 && activeTab === "activities",
  });

  const { data: trainingSessions = [] } = useQuery({
    queryKey: ["report-training", organization?.id],
    queryFn: async () => {
      // Sprint D aggregates: include _minor + currency for fee/cost cols.
      // G-062: participant_count was hardcoded 0 (false zeros in revenue math).
      // The datamodel HAS the real count — session_attendance rows with
      // rsvp_status='attending' (same predicate CoachDashboard uses).
      const { data } = await supabase
        .from("training_sessions")
        .select("id, status, player_fee, player_fee_minor, coach_cost_per_session, coach_cost_per_session_minor, court_cost_per_session, court_cost_per_session_minor, currency, group:training_groups(name), session_attendance(rsvp_status)")
        .eq("organization_id", organization!.id);
      return (data ?? []).map((s: any) => ({
        id: s.id,
        status: s.status,
        player_fee: s.player_fee,
        player_fee_minor: s.player_fee_minor,
        coach_cost_per_session: s.coach_cost_per_session,
        coach_cost_per_session_minor: s.coach_cost_per_session_minor,
        court_cost_per_session: s.court_cost_per_session,
        court_cost_per_session_minor: s.court_cost_per_session_minor,
        currency: s.currency,
        group_name: s.group?.name ?? null,
        participant_count: (s.session_attendance ?? []).filter((a: any) => a.rsvp_status === "attending").length,
      }));
    },
    enabled: !!organization?.id && activeTab === "activities",
  });

  const courtList = (courts || []).filter((c: any) => c.status === "active").map((c: any) => ({ id: c.id, name: c.name }));

  return (
    <div className="space-y-4">
      {/* Date range picker + report button */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <ReportDateRangePicker
          value={range}
          onChange={setRange}
          compareEnabled={compareEnabled}
          onCompareToggle={setCompareEnabled}
        />
        <ManagementReport range={range} compareRange={compareEnabled ? compRange : undefined} />
      </div>

      {/* Report tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full">
          <TabsTrigger value="dashboard" className="flex-1 gap-1 text-xs"><LayoutDashboard className="h-3.5 w-3.5" /> {t("reportTabs.dashboard")}</TabsTrigger>
          <TabsTrigger value="revenue" className="flex-1 gap-1 text-xs"><DollarSign className="h-3.5 w-3.5" /> {t("reportTabs.revenue")}</TabsTrigger>
          <TabsTrigger value="occupancy" className="flex-1 gap-1 text-xs"><BarChart3 className="h-3.5 w-3.5" /> {t("reportTabs.occupancy")}</TabsTrigger>
          <TabsTrigger value="players" className="flex-1 gap-1 text-xs"><UserCheck className="h-3.5 w-3.5" /> {t("reportTabs.players")}</TabsTrigger>
          <TabsTrigger value="membership" className="flex-1 gap-1 text-xs"><Crown className="h-3.5 w-3.5" /> {t("reportTabs.membership")}</TabsTrigger>
          <TabsTrigger value="activities" className="flex-1 gap-1 text-xs"><Trophy className="h-3.5 w-3.5" /> {t("reportTabs.leagues")}</TabsTrigger>
          <TabsTrigger value="budget" className="flex-1 gap-1 text-xs"><Calculator className="h-3.5 w-3.5" /> {t("reportTabs.budget")}</TabsTrigger>
          <TabsTrigger value="reconciliation" className="flex-1 gap-1 text-xs"><Receipt className="h-3.5 w-3.5" /> {t("reportTabs.reconciliation")}</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-3">
          <DashboardTab
            range={range}
            bookings={bookings}
            prevBookings={prevBookings}
            payments={payments}
            prevPayments={prevPayments}
            matchiPayments={matchiPayments}
            memberships={memberships}
            noShows={noShows}
            courtCount={courtCount}
            hallHours={hallHours}
            compareEnabled={compareEnabled}
          />
        </TabsContent>

        <TabsContent value="revenue" className="mt-3">
          <RevenueTab range={range} payments={payments} matchiPayments={matchiPayments} prevPayments={prevPayments} compareEnabled={compareEnabled} bookings={bookings} courts={courtList} />
        </TabsContent>

        <TabsContent value="occupancy" className="mt-3">
          <OccupancyTab range={range} bookings={bookings} prevBookings={prevBookings} courtCount={courtCount} hallHours={hallHours} courts={courtList} compareEnabled={compareEnabled} payments={payments} />
        </TabsContent>

        <TabsContent value="players" className="mt-3">
          <PlayersTab range={range} bookings={bookings} profiles={profiles} userCount={userCount} compareEnabled={compareEnabled} />
        </TabsContent>

        <TabsContent value="membership" className="mt-3">
          <MembershipTab range={range} memberships={memberships} compareEnabled={compareEnabled} />
        </TabsContent>

        <TabsContent value="activities" className="mt-3">
          <ActivitiesTab
            leagues={leagues}
            leagueMatches={leagueMatches}
            tournaments={tournaments}
            tournamentPlayers={tournamentPlayers}
            trainingSessions={trainingSessions}
          />
        </TabsContent>

        <TabsContent value="budget" className="mt-3">
          <BudgetTab />
        </TabsContent>

        <TabsContent value="reconciliation" className="mt-3">
          <PaymentReconciliation />
        </TabsContent>
      </Tabs>
    </div>
  );
}
